import { useCallback, useMemo } from 'react'

import { useMutation, useQuery } from '@apollo/client/react'

import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

export const GetEmailNotificationPreferencesQuery = graphql(`
  query getEmailNotificationPreferencesQuery {
    emailNotificationPreferences {
      __typename
      ... on EmailNotificationPreferencesResponse {
        preferences {
          id
          name
          description
          subscribed
          cioTopicId
        }
      }
      ... on EmailNotificationPreferenceError {
        message
      }
    }
  }
`)

export const UpdateEmailNotificationPreferencesMutation = graphql(`
  mutation emailNotificationPreferences(
    $input: [EmailNotificationPreferenceInput!]!
  ) {
    emailNotificationPreferences(input: $input) {
      __typename
      ... on EmailNotificationPreferencesResponse {
        preferences {
          id
          name
          description
          subscribed
          cioTopicId
        }
      }
      ... on EmailNotificationPreferenceError {
        message
      }
    }
  }
`)

type NotificationOptionProps = {
  id: string
  subscribed: boolean
}

/**
 * A single email notification preference, derived from the `preferences`
 * selection on {@link GetEmailNotificationPreferencesQuery}.
 */
export type EmailNotificationPreference = NonNullable<
  Extract<
    ResultOf<
      typeof GetEmailNotificationPreferencesQuery
    >['emailNotificationPreferences'],
    { __typename: 'EmailNotificationPreferencesResponse' }
  >['preferences']
>[number]

// Split into two groups to allow the comment notification items (hourlyEmails) in between
export const ACTIVITY_ALERTS_GROUP_BEFORE = [
  'postFailure',
  'channelConnectionUpdates',
] as const

export const ACTIVITY_ALERTS_GROUP_AFTER = [
  'collaboration',
  'postPublished',
  'queue',
  'billing',
] as const

// Combined group for skeleton loading
export const ACTIVITY_ALERTS_GROUP = [
  ...ACTIVITY_ALERTS_GROUP_BEFORE,
  ...ACTIVITY_ALERTS_GROUP_AFTER,
] as const

// Feature-flagged comment notifications (rendered between BEFORE and AFTER)
export const COMMENT_NOTIFICATIONS_GROUP = ['hourlyEmails'] as const

export const INSIGHTS_PERFORMANCE_GROUP = [
  'dailyPostRecap',
  'dailyCommunityRecap',
  'weeklyAnalyticsReport',
  'reminders',
] as const

export const TIPS_EDUCATION_GROUP = [
  'gettingStarted',
  'productUpdatesAndNews',
] as const

export const NEWSLETTERS_GROUP = [
  'newsletter',
  'openBlogNewsletter',
  'bufferCareers',
] as const

export const RESEARCH_FEEDBACK_GROUP = ['userFeedbackAndResearch'] as const

// Filter and sort the preferences based on the order of the GROUP array
function filterAndSortPreferences(
  preferences: EmailNotificationPreference[],
  orderArray: readonly string[],
): EmailNotificationPreference[] {
  return preferences
    ?.filter((preference) => orderArray.includes(preference.id))
    .toSorted((a, b) => orderArray.indexOf(a.id) - orderArray.indexOf(b.id))
}

export const useEmailNotificationPreferences = (): {
  preferencesList: EmailNotificationPreference[] | null
  activityAlertsBeforePreferences: EmailNotificationPreference[]
  activityAlertsAfterPreferences: EmailNotificationPreference[]
  commentNotificationsPreferences: EmailNotificationPreference[]
  insightsPerformancePreferences: EmailNotificationPreference[]
  tipsEducationPreferences: EmailNotificationPreference[]
  newslettersPreferences: EmailNotificationPreference[]
  researchFeedbackPreferences: EmailNotificationPreference[]
  updateNotificationSetting: (
    options: NotificationOptionProps,
  ) => Promise<boolean>
  loading: boolean
  error: Error | undefined
} => {
  const { data, loading, error } = useQuery(
    GetEmailNotificationPreferencesQuery,
  )
  const [updateEmailNotificationPreferences] = useMutation(
    UpdateEmailNotificationPreferencesMutation,
  )

  const updateNotificationSetting = useCallback(
    async (options: NotificationOptionProps): Promise<boolean> => {
      try {
        await updateEmailNotificationPreferences({
          variables: {
            input: [
              {
                id: options.id,
                subscribed: options.subscribed,
              },
            ],
          },
        })
        return true
      } catch {
        toast.error(
          'Sorry! There was an issue updating your email preferences. Please try again.',
        )
        return false
      }
    },
    [updateEmailNotificationPreferences],
  )

  const preferencesList =
    data?.emailNotificationPreferences.__typename ===
    'EmailNotificationPreferencesResponse'
      ? data?.emailNotificationPreferences.preferences
      : null

  const groupedPreferences = useMemo(() => {
    const list = preferencesList ?? []
    return {
      activityAlertsBeforePreferences: filterAndSortPreferences(
        list,
        ACTIVITY_ALERTS_GROUP_BEFORE,
      ),
      activityAlertsAfterPreferences: filterAndSortPreferences(
        list,
        ACTIVITY_ALERTS_GROUP_AFTER,
      ),
      commentNotificationsPreferences: filterAndSortPreferences(
        list,
        COMMENT_NOTIFICATIONS_GROUP,
      ),
      insightsPerformancePreferences: filterAndSortPreferences(
        list,
        INSIGHTS_PERFORMANCE_GROUP,
      ),
      tipsEducationPreferences: filterAndSortPreferences(
        list,
        TIPS_EDUCATION_GROUP,
      ),
      newslettersPreferences: filterAndSortPreferences(list, NEWSLETTERS_GROUP),
      researchFeedbackPreferences: filterAndSortPreferences(
        list,
        RESEARCH_FEEDBACK_GROUP,
      ),
    }
  }, [preferencesList])

  return {
    preferencesList,
    ...groupedPreferences,
    updateNotificationSetting,
    loading,
    error,
  }
}
